import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../store/store';
import { getNotifications } from '../rtk-apis/reports/getNotifications';
import { markNotificationRead } from '../rtk-apis/reports/markNotificationRead';
import { markAllNotificationsRead } from '../rtk-apis/reports/markAllNotificationsRead';
import type { AppNotification } from '../store/slices/reports/getNotifications';
import { initWebPush } from '../firebaseMessaging';

const browserNotificationsSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window;

/** "2m ago" / "3h ago" / "5d ago" from an ISO date. */
function timeAgo(iso?: string): string {
  if (!iso) {
    return '';
  }
  const then = new Date(iso).getTime();
  if (isNaN(then)) {
    return '';
  }
  const mins = Math.floor((Date.now() - then) / 60_000);
  if (mins < 1) {
    return 'just now';
  }
  if (mins < 60) {
    return `${mins}m ago`;
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const NotificationBell = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { notifications, unreadCount, loading } = useSelector(
    (state: RootState) => state.getNotificationsResponse
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    browserNotificationsSupported() ? Notification.permission : 'unsupported'
  );
  // FCM web push is registered once per mount cycle (survives route changes via
  // the module-level messaging singleton; re-registering the same token is a no-op).
  const webPushInitRef = useRef(false);

  const requestPermission = () => {
    if (!browserNotificationsSupported()) {
      return;
    }
    Notification.requestPermission().then(p => setPermission(p));
  };

  // Fetch once when the header mounts. Subsequent badge updates arrive through
  // FCM or an explicit bell click; polling this read endpoint created a noisy
  // audit event every minute for every open browser tab.
  useEffect(() => {
    dispatch(getNotifications());
    if (browserNotificationsSupported() && Notification.permission === 'default') {
      requestPermission();
    }
  }, [dispatch]);

  // Once permission is granted: register the FCM service worker + web token.
  // Background pushes (tab hidden/closed) display via firebase-messaging-sw.js;
  // foreground pushes arrive here — sync the badge and show the OS toast ourselves
  // (FCM never auto-displays while the page is focused).
  useEffect(() => {
    if (permission !== 'granted' || webPushInitRef.current) {
      return;
    }
    webPushInitRef.current = true;
    initWebPush(payload => {
      dispatch(getNotifications());
      const title = payload?.notification?.title || payload?.data?.title || 'Cardiac Health';
      const body = payload?.notification?.body || payload?.data?.body || '';
      const reportId = payload?.data?.reportId;
      try {
        const toast = new Notification(title, {
          body,
          tag: payload?.data?.notificationId || undefined,
          icon: '/assets/android-chrome-192x192.png',
        });
        toast.onclick = () => {
          window.focus();
          if (reportId) {
            navigate(`/patientReports/${reportId}`);
          }
          toast.close();
        };
      } catch (_) {
        /* constructor unsupported (e.g. some Android browsers) — badge still updates */
      }
    });
  }, [permission, dispatch, navigate]);

  const open = Boolean(anchorEl);
  const showEnableNote = permission === 'default' || permission === 'denied';

  const sorted = useMemo(
    () =>
      [...notifications].sort(
        (a, b) =>
          new Date(b.createdAt || b.createdOn || 0).getTime() -
          new Date(a.createdAt || a.createdOn || 0).getTime()
      ),
    [notifications]
  );

  const onItemClick = (n: AppNotification) => {
    if (!n.read) {
      dispatch(markNotificationRead(n._id));
    }
    if (n.reportId) {
      setAnchorEl(null);
      navigate(`/patientReports/${n.reportId}`);
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={e => {
            setAnchorEl(e.currentTarget);
            dispatch(getNotifications());
          }}
          // Counter the header's 16px flex gap + the IconButton's own 8px padding so
          // the bell sits snug against the nav pills instead of floating mid-gap.
          sx={{ ml: -2, mr: -1, color: '#9fb2cc', '&:hover': { color: '#2dd4bf' } }}
        >
          <Badge
            badgeContent={unreadCount}
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#2dd4bf',
                color: '#00101f',
                fontWeight: 700,
              },
            }}
          >
            <NotificationsNoneOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 380,
              maxWidth: '92vw',
              backgroundColor: '#16263b',
              border: '1px solid #314a6b',
              borderRadius: '14px',
              boxShadow: '0 22px 54px -18px rgba(0,0,0,0.9)',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#e2e8f0', flex: 1 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={() => dispatch(markAllNotificationsRead())}
              sx={{ fontSize: 12, textTransform: 'none', color: '#2dd4bf' }}
            >
              Mark all as read
            </Button>
          )}
        </Box>
        <Divider sx={{ borderColor: '#243652' }} />

        {showEnableNote && (
          <Box
            sx={{
              m: 1.5,
              p: 1.5,
              borderRadius: '10px',
              backgroundColor: 'rgba(45,212,191,.08)',
              border: '1px solid rgba(45,212,191,.35)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <NotificationsActiveOutlinedIcon sx={{ color: '#2dd4bf', fontSize: 20, mt: '2px' }} />
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', mb: 0.5 }}>
                  Stay on top of your reports
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: '#9fb2cc', lineHeight: 1.5 }}>
                  Enable browser notifications to be alerted on this device the moment a report
                  becomes overdue or requires your attention — even when this tab is in the
                  background. Your browser will ask for permission once, and you can change this at
                  any time in your browser settings.
                </Typography>
                {permission === 'default' ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={requestPermission}
                    sx={{
                      mt: 1,
                      fontSize: 12,
                      textTransform: 'none',
                      color: '#2dd4bf',
                      borderColor: 'rgba(45,212,191,.5)',
                      '&:hover': { borderColor: '#2dd4bf', backgroundColor: 'rgba(45,212,191,.1)' },
                    }}
                  >
                    Enable notifications
                  </Button>
                ) : (
                  <Typography sx={{ fontSize: 12, color: '#fbbf24', mt: 1 }}>
                    Notifications are currently blocked for this site. To enable them, open your
                    browser&apos;s site settings and allow notifications for this page.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {loading && sorted.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress
                size={22}
                sx={{ color: '#2dd4bf' }}
              />
            </Box>
          ) : sorted.length === 0 ? (
            <Typography sx={{ px: 2, py: 4, fontSize: 13, color: '#5b6b84', textAlign: 'center' }}>
              You&apos;re all caught up — no notifications yet.
            </Typography>
          ) : (
            sorted.map(n => (
              <Box
                key={n._id}
                onClick={() => onItemClick(n)}
                sx={{
                  px: 2,
                  py: 1.25,
                  cursor: n.reportId || !n.read ? 'pointer' : 'default',
                  backgroundColor: n.read ? 'transparent' : 'rgba(45,212,191,.06)',
                  borderBottom: '1px solid #1d2f47',
                  '&:hover': { backgroundColor: 'rgba(45,212,191,.1)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {!n.read && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#2dd4bf',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      fontWeight: n.read ? 500 : 700,
                      color: n.read ? '#9fb2cc' : '#e2e8f0',
                      flex: 1,
                    }}
                  >
                    {n.title}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: '#5b6b84', flexShrink: 0 }}>
                    {timeAgo(n.createdAt || n.createdOn)}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 12.5, color: '#9fb2cc', mt: 0.25, lineHeight: 1.45 }}>
                  {n.body}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
